import type { DataProcessResult } from '@/services/file.service';

export type CsvImporterColumn = {
  fullWidth?: boolean;
  key: string;
  label: string;
  regexp: RegExp;
  required: boolean;
  tooltipText?: string;
};

/**
 * Columns are rendered in labelled sections, each optionally introduced by a
 * toast. A group without a label renders as a bare grid.
 */
export type CsvImporterColumnGroup = {
  columns: CsvImporterColumn[];
  description?: string;
  key: string;
  label?: string;
  notice?: {
    secondaryText?: string;
    text: string;
  };
};

export type CsvMapping = {
  fileCol: string;
  key: string;
};

export type CsvImporterConfig = {
  delimiter: 'comma' | 'semicolon' | 'space' | 'tab';
  separator: 'comma' | 'dot';
};

export const DELIMITER_CHARS: Record<string, string> = {
  comma: ',',
  dot: '.',
  semicolon: ';',
  space: ' ',
  tab: '\t'
};

/**
 * Auto-matches platform columns to file headers by regexp, preserving any
 * mapping the user already made. A file header is never assigned twice.
 */
export function buildInitialMapping(
  columns: CsvImporterColumn[],
  headers: string[],
  existing: CsvMapping[]
): CsvMapping[] {
  const mapping: CsvMapping[] = [];
  const assignedHeaders = new Set<string>();

  columns.forEach(column => {
    const userSelected = existing.find(entry => entry.key === column.key && headers.includes(entry.fileCol));
    if (userSelected) {
      mapping.push(userSelected);
      assignedHeaders.add(userSelected.fileCol);
      return;
    }

    const matchedHeader = headers.find(
      header => !assignedHeaders.has(header) && new RegExp(column.regexp, 'i').test(header)
    );

    if (matchedHeader) {
      mapping.push({ fileCol: matchedHeader, key: column.key });
      assignedHeaders.add(matchedHeader);
    }
  });

  return mapping;
}

/**
 * When the chosen delimiter does not match the file, rows come back with a
 * different column count than the header row. Pad both to the widest row so
 * the preview table stays rectangular.
 */
export function normalizePreview(preprocess?: DataProcessResult) {
  const headers = preprocess?.headers ?? [];
  const dataPreview = preprocess?.dataPreview ?? [];
  const hasDelimiterError = dataPreview.some(row => row.length !== headers.length);

  if (!hasDelimiterError) {
    return {
      hasDelimiterError: false,
      normalizedDataPreview: dataPreview,
      normalizedHeaders: headers
    };
  }

  const maxCols = Math.max(headers.length, ...dataPreview.map(row => row.length));

  return {
    hasDelimiterError: true,
    normalizedDataPreview: dataPreview.map(row => [...row, ...new Array<string>(maxCols - row.length).fill('')]),
    normalizedHeaders: [...headers, ...new Array<string>(maxCols - headers.length).fill('')]
  };
}

const LINE_FEED = 10;
const CARRIAGE_RETURN = 13;

/**
 * Removes the rows with no value in any column (blank, whitespace-only or delimiter-only), with the same rule and
 * line breaks (\n, \r\n, \r) as the backend preprocess. Works on bytes so the file encoding is kept as it is.
 */
export async function removeEmptyRows(file: File, delimiter: string): Promise<{ file: File; removed: number }> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const blankBytes = new Set([delimiter, '"', ' ', '\t'].map(char => char.charCodeAt(0)));
  const keptLines: Uint8Array<ArrayBuffer>[] = [];
  let removed = 0;
  let start = 0;

  while (start < bytes.length) {
    let end = start;
    while (end < bytes.length && bytes[end] !== LINE_FEED && bytes[end] !== CARRIAGE_RETURN) {
      end++;
    }
    let next = end < bytes.length ? end + 1 : end;
    if (bytes[end] === CARRIAGE_RETURN && bytes[next] === LINE_FEED) {
      next++;
    }

    const isHeader = start === 0;
    if (!isHeader && bytes.subarray(start, end).every(byte => blankBytes.has(byte))) {
      removed++;
    } else {
      keptLines.push(bytes.subarray(start, next));
    }
    start = next;
  }

  return { file: new File(keptLines, file.name, { type: file.type }), removed };
}
